import _ from 'lodash';
import Submission from 'edge/quoteandbind/common/models/Submission';

export default ['$q',
    'JsonRPCService',
    'qnb.ErrorHandlingService',
    'qnb.endpointConfig',
    'qnb.xCenterRouteConfig', ($q, JsonRPCService, ErrorHandlingService, endpointConfig, xCenterRouteConfig) => {

        const xCenterRoute = xCenterRouteConfig;

        /**
         * @param {Object} params
         * @param {Object} params.submissionDTO
         * @param {String} params.processName
         * @param {Boolean} params.handleErrorBeforeRequest
         * @param {Boolean} params.setDTOBeforeHandling
         * @returns {Promise}
         */
        function processSubmission(params) {
            let submissionDTO = params.submissionDTO;
            const handleErrorBeforeRequest = params.handleErrorBeforeRequest;
            const setDTOBeforeHandling = params.setDTOBeforeHandling;

            const deferred = $q.defer();
            if (handleErrorBeforeRequest) {
                ErrorHandlingService.checkSessionUUID(submissionDTO, deferred);
            }

            const endpoint = endpointConfig.loadSaveEndpoint;
            JsonRPCService.send(xCenterRoute, endpoint, params.processName, [submissionDTO]).then(
                (response) => {
                    if (!handleErrorBeforeRequest) {
                        if (setDTOBeforeHandling) {
                            submissionDTO = new Submission(response);
                        }
                        ErrorHandlingService.checkSessionUUID(submissionDTO, deferred);
                        if (!setDTOBeforeHandling) {
                            submissionDTO = new Submission(response);
                        }
                    } else {
                        submissionDTO = new Submission(response);
                    }
                    return deferred.resolve(submissionDTO);
                },
                handleError(deferred)
            );
            return deferred.promise;
        }

        function setQuoteToDraft(quoteID, sessionUUID, periodStartDate) {
            const deferred = $q.defer();
            const endpoint = endpointConfig.loadSaveEndpoint;
            JsonRPCService.send(xCenterRoute, endpoint, 'setQuoteToDraft', [quoteID, sessionUUID]).then(
                (response) => {
                    ErrorHandlingService.checkSessionUUID({
                        sessionUUID
                    }, deferred);
                    if (periodStartDate != undefined) {
                        response.baseData.periodStartDate = periodStartDate;   
                    }
                    const submissionDTO = new Submission(response);
                    return deferred.resolve(submissionDTO);
                },
                handleError(deferred)
            );
            return deferred.promise;
        }

        function handleError(deferred) {
            return (error, status, headers) => {
                return deferred.reject([error, status, headers]);
            };
        }

        function isSubmissionQuoted(submissionDTO) {
            return submissionDTO.quoteData && _.some(submissionDTO.quoteData.offeredQuotes, quote => quote.status === 'Quoted');
        }

        /**
         * Set the submission to draft if in quoted state,
         * it returns the submission otherwise
         * @param {Object} submissionDTO the submission that needs to be draft
         * @returns {Promise} a promise to set the quote to draft
         */
        function ensureIsSetToDraft(submissionDTO) {
            if (isSubmissionQuoted(submissionDTO)) {
                return setQuoteToDraft(submissionDTO.quoteID, submissionDTO.sessionUUID,  submissionDTO.baseData.periodStartDate);
            }
            return $q.resolve(submissionDTO);
        }

        function updateDraftSubmission(submissionDTO) {
            return processSubmission({
                submissionDTO: submissionDTO,
                processName: 'updateDraftSubmission',
                handleErrorBeforeRequest: false,
                setDTOBeforeHandling: false
            });
        }

        return {
            createSubmission: (submissionDTO) => {
                return processSubmission({
                    submissionDTO,
                    processName: 'create',
                    handleErrorBeforeRequest: false,
                    setDTOBeforeHandling: true
                });
            },
            saveAndQuoteSubmission: (submissionDTO) => {
                return processSubmission({
                    submissionDTO,
                    processName: 'saveAndQuote',
                    handleErrorBeforeRequest: false,
                    setDTOBeforeHandling: false
                });
            },
            updateDraftSubmission: updateDraftSubmission,
            saveSubmission: (submissionDTO) => {
                return ensureIsSetToDraft(submissionDTO)
                    .then(updateDraftSubmission);
            },
            updateDraftSubmissionAndPrimaryLocation: (submissionDTO) => {
                const submissionParameters = {
                    submissionDTO: submissionDTO,
                    processName: 'updateDraftSubmissionAndPrimaryLocation',
                    handleErrorBeforeRequest: false,
                    setDTOBeforeHandling: false
                };

                if (submissionDTO.quoteData && _.some(submissionDTO.quoteData.offeredQuotes, quote => quote.status === 'Quoted')) {
                    const deferred = $q.defer();
                    setQuoteToDraft(submissionDTO.quoteID, submissionDTO.sessionUUID).then(() => {
                        processSubmission(submissionParameters).then(updateResponse => {
                            deferred.resolve(updateResponse);
                        }, handleError(deferred));
                    }, handleError(deferred));
                    return deferred.promise;
                }
                return processSubmission(submissionParameters);
            },
            withdrawSubmission_PSA: (jobNumber) => {
                // PSA: duplication of JobService.withdrawJobByJobNumber(...)
                // Didn't want to introduce dependancy from QB modules to GPA modules
                return JsonRPCService.send('pc', 'gateway/submission', 'withdrawJobByJobNumber', [jobNumber]);
            },
            retrievePaymentPlans: (submissionDTO) => {
                const endpoint = endpointConfig.loadSaveEndpoint;
                return JsonRPCService.send(xCenterRoute, endpoint, 'retrievePaymentPlans', [submissionDTO.quoteID, submissionDTO.sessionUUID]);
            },
            quoteSubmission: (quoteID, sessionUUID) => {
                let submissionDTO;
                const deferred = $q.defer();
                const endpoint = endpointConfig.loadSaveEndpoint;
                JsonRPCService.send(xCenterRoute, endpoint, 'quote', [quoteID, sessionUUID]).then(
                    (response) => {
                        ErrorHandlingService.checkSessionUUID({
                            sessionUUID
                        }, deferred);
                        submissionDTO = new Submission(response);
                        return deferred.resolve(submissionDTO);
                    },
                    (error, status, headers) => {
                        return deferred.reject([error, status, headers]);
                    }
                );
                return deferred.promise;
            },
            setQuoteToDraft: setQuoteToDraft,
            updateSubmissionCoverages: (quoteID, lobCoverages, sessionUUID) => {
                const endpoint = endpointConfig.loadSaveEndpoint;
                return JsonRPCService.send(xCenterRoute, endpoint, 'updateCoverages', [quoteID, sessionUUID, lobCoverages]);
            },
            saveQuotedSubmission: (submissionDTO) => {
                return processSubmission({
                    submissionDTO,
                    processName: 'updateQuotedSubmission',
                    handleErrorBeforeRequest: true,
                    setDTOBeforeHandling: false
                });
            },
            saveQuotedSubmissionWithLOBData: (submissionDTO) => {
                return processSubmission({
                    submissionDTO,
                    processName: 'updateQuotedSubmissionWithLOBData',
                    handleErrorBeforeRequest: true,
                    setDTOBeforeHandling: false
                });
            },
            bindSubmission: (submissionDTO) => {
                return processSubmission({
                    submissionDTO,
                    processName: 'bind',
                    handleErrorBeforeRequest: false,
                    setDTOBeforeHandling: false
                });
            },
            retrieveSubmission: (submissionDTO) => {
                return processSubmission({
                    submissionDTO,
                    processName: 'retrieve',
                    handleErrorBeforeRequest: false,
                    setDTOBeforeHandling: false
                });
            },
            createEmptySubmission: (submission) => {
                const deferred = $q.defer();
                JsonRPCService.send(xCenterRoute, endpointConfig.loadSaveEndpoint, 'create', [submission])
                    .then((response) => {
                        const submissionResponseDTO = response;
                        return deferred.resolve(submissionResponseDTO);
                    }, (error, status, headers) => {
                        return deferred.reject([error, status, headers]);
                    });
                return deferred.promise;
            }
        };
    }
];
